import { type RowData } from '@tanstack/react-table';
import { type LegacyReactTable as Table } from '@tanstack/react-table/legacy';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import { IconButton } from '#components/Input/IconButton';
import { ChevronRight } from '#icons/ChevronRight';
import { ChevronLeft } from '#icons/ChevronLeft';
import { PageNumbers } from './PageNumbers';
import { ResultsPerPage, resultsPerPageOptions } from './ResultsPerPage';
import { PaginationButtonWrapper, PaginationWrapper, ShowingRowsWrapper } from './styles';

type PaginationProps<T extends RowData> = {
  table: Table<T>;
};

export const Pagination = <T extends RowData>({ table }: PaginationProps<T>) => {
  const tableRef = useRef(table);
  const pageCount = tableRef.current.getPageCount(); //Hacky workaround to solve react19 issues with react table

  const currentPage = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const totalRows = table.getRowCount();
  const currentRows = table.getRowModel().rows.length;
  const startRow = totalRows === 0 ? 0 : currentPage * pageSize + 1;
  const endRow = Math.min(totalRows, currentPage * pageSize + currentRows);
  const { t } = useTranslation();
  const options = useMemo(() => resultsPerPageOptions(totalRows), [totalRows]);
  const minimumResultsPerPage = Math.min(...options);

  useEffect(() => {
    if (!options.includes(pageSize)) {
      const newOption = options[1] || minimumResultsPerPage;

      table.setPageSize(newOption);
    }
  }, [options, pageSize, minimumResultsPerPage, table]);

  if (totalRows <= minimumResultsPerPage) {
    return null;
  }

  return (
    <PaginationWrapper aria-label="Pagination Navigation">
      <ShowingRowsWrapper>
        {t('table.pagination.showing-rows', {
          rows: `${startRow}-${endRow}`,
          totalRows: totalRows.toLocaleString(),
        })}
      </ShowingRowsWrapper>
      <PaginationButtonWrapper>
        <IconButton
          variant="secondary"
          aria-label="Previous page"
          onClick={() => {
            table.previousPage();
          }}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft />
        </IconButton>
        <PageNumbers
          currentPage={currentPage}
          pageCount={pageCount}
          onClick={(index) => {
            table.setPageIndex(index);
          }}
        />
        <IconButton
          variant="secondary"
          aria-label="Next page"
          onClick={() => {
            table.nextPage();
          }}
          disabled={!table.getCanNextPage()}
        >
          <ChevronRight />
        </IconButton>
      </PaginationButtonWrapper>

      <ResultsPerPage
        totalRows={totalRows}
        pageSize={pageSize}
        onChange={(newPageSize) => {
          table.setPageSize(newPageSize);
        }}
      />
    </PaginationWrapper>
  );
};
